import React from "react";
import { Formik } from "formik";
import {
    TextField,
    withStyles,
    Button,
    CircularProgress,
    MenuItem
} from "@material-ui/core";
import * as Yup from "yup";

const KEYBOARD_LAYOUTS = [
    { code: "en", name: "US", family: "QWERTY" },
    { code: "en-gb", name: "United Kingdom", family: "QWERTY" },
    { code: "en-intl", name: "US International", family: "QWERTY" },
    { code: "de", name: "German", family: "QWERTZ" },
    { code: "de-ch", name: "Swiss German", family: "QWERTZ" },
    { code: "fr", name: "French", family: "AZERTY" },
    { code: "fr-ch", name: "Swiss French", family: "QWERTZ" },
    { code: "fr-ca", name: "Canadian French", family: "QWERTY" },
    { code: "be", name: "Belgian", family: "AZERTY" },
    { code: "nl", name: "Dutch", family: "QWERTY" },
    { code: "es", name: "Spanish", family: "QWERTY" },
    { code: "es-la", name: "Latin American Spanish", family: "QWERTY" },
    { code: "it", name: "Italian", family: "QWERTY" },
    { code: "pt", name: "Portuguese", family: "QWERTY" },
    { code: "pt-br", name: "Brazilian", family: "ABNT2" },
    { code: "sv", name: "Swedish", family: "QWERTY" },
    { code: "no", name: "Norwegian", family: "QWERTY" },
    { code: "da", name: "Danish", family: "QWERTY" },
    { code: "fi", name: "Finnish", family: "QWERTY" },
    { code: "is", name: "Icelandic", family: "QWERTY" },
    { code: "pl", name: "Polish", family: "QWERTY" },
    { code: "cs", name: "Czech", family: "QWERTZ" },
    { code: "sk", name: "Slovak", family: "QWERTZ" },
    { code: "hu", name: "Hungarian", family: "QWERTZ" },
    { code: "ro", name: "Romanian", family: "QWERTY" },
    { code: "sl", name: "Slovenian", family: "QWERTZ" },
    { code: "hr", name: "Croatian", family: "QWERTZ" },
    { code: "sr", name: "Serbian", family: "QWERTZ" },
    { code: "bg", name: "Bulgarian", family: "Cyrillic" },
    { code: "et", name: "Estonian", family: "QWERTY" },
    { code: "lv", name: "Latvian", family: "QWERTY" },
    { code: "lt", name: "Lithuanian", family: "QWERTY" },
    { code: "el", name: "Greek", family: "Greek" },
    { code: "tr", name: "Turkish-Q", family: "QWERTY" },
    { code: "tr-f", name: "Turkish-F", family: "Turkish-F" },
    { code: "mk", name: "Macedonian", family: "Cyrillic" },
    { code: "sq", name: "Albanian", family: "QWERTZ" },
    { code: "ru", name: "Russian", family: "Cyrillic" },
    { code: "uk", name: "Ukrainian", family: "Cyrillic" },
    { code: "be-by", name: "Belarusian", family: "Cyrillic" },
    { code: "kk", name: "Kazakh", family: "Cyrillic" },
    { code: "ar", name: "Arabic", family: "Arabic" },
    { code: "he", name: "Hebrew", family: "Hebrew" },
    { code: "fa", name: "Persian (Farsi)", family: "Arabic" },
    { code: "ur", name: "Urdu", family: "Arabic" },
    { code: "ja", name: "Japanese", family: "JIS" },
    { code: "ko", name: "Korean", family: "Hangul" },
    { code: "zh", name: "Chinese", family: "Chinese" },
    { code: "th", name: "Thai", family: "Thai" },
    { code: "km", name: "Khmer", family: "Khmer" },
    { code: "lo", name: "Lao", family: "Lao" },
    { code: "my", name: "Burmese (Myanmar)", family: "Myanmar" },
    { code: "hi", name: "Hindi", family: "Indic" },
    { code: "bn", name: "Bengali", family: "Indic" },
    { code: "ta", name: "Tamil", family: "Indic" },
    { code: "te", name: "Telugu", family: "Indic" },
    { code: "kn", name: "Kannada", family: "Indic" },
    { code: "ml", name: "Malayalam", family: "Indic" },
    { code: "gu", name: "Gujarati", family: "Indic" },
    { code: "pa", name: "Punjabi (Gurmukhi)", family: "Indic" },
    { code: "si", name: "Sinhala", family: "Indic" },
    { code: "ne", name: "Nepali", family: "Indic" },
    { code: "ka", name: "Georgian", family: "Georgian" },
    { code: "hy", name: "Armenian", family: "Armenian" },
    { code: "az", name: "Azerbaijani", family: "QWERTY" },
    { code: "mn", name: "Mongolian", family: "Cyrillic" }
];

const styles = theme => ({
    root: {
        width: "auto",
        height: "auto",
        padding: "2%",
        margin: "2%"
    },
    rootEdit: {
        flexGrow: 1,
        paddingTop: theme.spacing(2)
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "120px",
        marginTop: "10px"
    },
    cancel: {
        width: "120px",
        marginTop: "10px",
        marginRight: "10px",
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default

        }
    },
    formControl: {
        width: "100%"
    },
    buttons: {
        textAlign: "right"
    },
    screen: {
        display: "flex",
    },
});

const PCProductEditForm = props => {
    const { classes, formSettings, editable, initialValues, handleSubmit, translations } = props;

    return (
        <div className={classes.root}>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(true);

                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit } = formikProps;
                    return (
                        <form onSubmit={handleSubmit}>
                            <TextField
                                name="RAM"
                                value={values.RAM}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.RAM && touched.RAM)}
                                placeholder={translations['ram'] || 'RAM'}
                                label={translations['ram'] || 'RAM'}
                                margin={"dense"}
                                fullWidth={true}
                            />

                            <TextField
                                name="Version"
                                value={values.Version}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Version && touched.Version)}
                                placeholder={translations['version'] || 'Version'}
                                label={translations['version'] || 'Version'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="Webcam"
                                value={values.Webcam}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Webcam && touched.Webcam)}
                                placeholder={translations['webcam'] || 'Webcam'}
                                label={translations['webcam'] || 'Webcam'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="WebcamVendor"
                                value={values.WebcamVendor}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.WebcamVendor && touched.WebcamVendor)}
                                placeholder={translations['webcam_vendor'] || 'Webcam vendor'}
                                label={translations['webcam_vendor'] || 'Webcam vendor'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="Bluetooth"
                                value={values.Bluetooth}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Bluetooth && touched.Bluetooth)}
                                placeholder={translations['bluetooth'] || 'Bluetooth'}
                                label={translations['bluetooth'] || 'Bluetooth'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="BluetoothVendor"
                                value={values.BluetoothVendor}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.BluetoothVendor && touched.BluetoothVendor)}
                                placeholder={translations['bluetooth_vendor'] || 'Bluetooth vendor'}
                                label={translations['bluetooth_vendor'] || 'Bluetooth vendor'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <div className={classes.screen}>
                                <TextField
                                    name="Resolution"
                                    value={values.Resolution}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Resolution && touched.Resolution)}
                                    placeholder={translations['resolution'] || 'Resolution'}
                                    label={translations['resolution'] || 'Resolution'}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                                <TextField
                                    name="ScreenSize"
                                    value={values.ScreenSize}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.ScreenSize && touched.ScreenSize)}
                                    placeholder={translations['screen_size'] || 'Screen size'}
                                    label={translations['screen_size'] || 'Screen size'}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                            </div>
                            <TextField
                                name="OperatingSystem"
                                value={values.OperatingSystem}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.OperatingSystem && touched.OperatingSystem)}
                                placeholder={translations['operating_system'] || 'Operating system'}
                                label={translations['operating_system'] || 'Operating system'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="OemProductKey"
                                value={values.OemProductKey}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.OemProductKey && touched.OemProductKey)}
                                placeholder={translations['oem_product_key'] || 'OEM Product Key'}
                                label={translations['oem_product_key'] || 'OEM Product Key'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="NetworkStandard"
                                value={values.NetworkStandard}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.NetworkStandard && touched.NetworkStandard)}
                                placeholder={translations['network_standard'] || 'Network standard'}
                                label={translations['network_standard'] || 'Network standard'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="UUID"
                                value={values.UUID}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.UUID && touched.UUID)}
                                placeholder={translations['uuid'] || 'UUID'}
                                label={translations['uuid'] || 'UUID'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="SecurazeVersion"
                                value={values.SecurazeVersion}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.SecurazeVersion && touched.SecurazeVersion)}
                                placeholder={translations['securaze_version'] || 'Securaze version'}
                                label={translations['securaze_version'] || 'Securaze version'}
                                margin={"dense"}
                                fullWidth={true}
                                disabled
                            />
                            <TextField
                                name="HardwareID"
                                value={values.HardwareID}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.HardwareID && touched.HardwareID)}
                                placeholder={translations['hardware_id'] || 'HardwareID'}
                                label={translations['hardware_id'] || 'HardwareID'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                name="ANumber"
                                value={values.ANumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.ANumber && touched.ANumber)}
                                placeholder={translations['anumber'] || 'ANumber'}
                                label={translations['anumber'] || 'ANumber'}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <TextField
                                select
                                name="KeyboardLayoutCode"
                                value={values.KeyboardLayoutCode || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.KeyboardLayoutCode && touched.KeyboardLayoutCode)}
                                placeholder={translations['keyboard_layout'] || 'Keyboard layout'}
                                label={translations['keyboard_layout'] || 'Keyboard layout'}
                                margin={"dense"}
                                fullWidth={true}
                            >
                                {KEYBOARD_LAYOUTS.map(layout => (
                                    <MenuItem key={layout.code} value={layout.code}>
                                        {`${layout.name} (${layout.family})`}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {formSettings.ButtonSubmit ? (
                                <Button
                                    className={classes.submit}
                                    variant="contained"
                                    style={{ width: '100%' }}
                                    type="submit"
                                    disabled={!(isValid && dirty) || isSubmitting || !editable}
                                >
                                    {isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                </Button>
                            ) : null}
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default withStyles(styles, { withTheme: true })(PCProductEditForm);
